import React from 'react';
import { ComparisonSection } from '../components/ComparisonSection';
import AnimatedAurora from '../components/AnimatedAurora';

const HomePage: React.FC = () => {
  return (
    <div className="bg-white"> 
      <section className="relative py-28 lg:py-40 overflow-hidden bg-gradient-to-br from-primary-700 via-primary-600 to-primary-700">
        <AnimatedAurora className="absolute inset-0 -z-10" height={560} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 text-blue-100 mb-6">
              <span className="text-sm font-semibold">Built for Home Service Businesses</span>
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-wide text-white mb-6">
              Turn Missed Opportunities Into <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-cyan-200">Revenue</span>
            </h1>
            <p className="font-subheading text-xl text-blue-100 max-w-3xl mx-auto">
             Capture leads, engage customers instantly, and qualify jobs automatically with <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-cyan-200">Helpr</span>
            </p>
          </div>
        </div>
      </section>

      {/* Features Section - Replace with your Figma Features component */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Capture More Leads 24/7
            </h2>
            <p className="font-subheading text-xl text-gray-600 max-w-3xl mx-auto">
              Our smart assistant is built to turn visitors into customers 
              with helpful conversations and seamless lead capture.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Easy Integration</h3>
              <p className="text-gray-600">
                Add our widget to any website. No technical expertise required.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Smart Assistant</h3>
              <p className="text-gray-600">
                Intelligent conversations that guide visitors through your 
                sales funnel automatically.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Lead Tracking</h3>
              <p className="text-gray-600">
                Automatically capture and organize leads in Google Sheets 
                or your CRM system.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">SMS Notifications</h3>
              <p className="text-gray-600">
                Instant alerts for new leads, plus daily, weekly, or monthly summaries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <ComparisonSection />

      {/* CTA Section - Replace with your Figma CTA component */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Creating More Revenue?
          </h2>
          <p className="font-subheading text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Capture every lead. Even when you can't answer the phone.
          </p>
          <a
            href="mailto:luke@leadhelpr.com?subject=Get%20Started%20with%20Helpr&body=Hi%20Luke%2C%0A%0AI'm%20ready%20to%20get%20started%20with%20Helpr.%20Please%20reach%20out%20with%20next%20steps.%0A%0AThanks!"
            className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
          >
            Get Started Today
          </a>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
